import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../config.service';
import { FormQuestion, FormQuestionResponse, FormQuestionTypes, FormResponse, FormResponseOptionSelection, FormResponseReportLine, PpmFormWithQuestions, StaffEvalReportLine } from './types/forms';
import { Observable, of, switchMap } from 'rxjs';
import { FormResponseSearchParameters } from '../routed-modules/ppm-forms/form-response-filters/form-response-filters.component';
import { DataTransform, DateField } from './dataTransformFunctions';

import { TripService } from './trip.service';
import { TripDetail } from './commonInterfaces';

export interface FormQuestionColumn {
  header: string,
  longText?: string,
  field: string,
  type?: FormQuestionTypes,
  style?: any

}

export interface TripResponseReportData {
  data: FormResponseReportLine[],
  form: PpmFormWithQuestions
}

export interface PartnerPostTripSurveyStatus {
  responseId: number;
  contactId: string;
  partnerName: string;
  contactName: string;
  email: string;
  isSubmitted: number;
  emailRequestSent: number;
  statusLabel: string;
  dateCreated: Date;
  dateSubmitted: Date | null;
  dateReminded: Date | null;
}

export interface TripLinkedFormResponseSurveyRow {
  formResponseTripId: number;
  formResponseId: number;
  formId: number;
  formName: string;
  responder: string;
  responderType: string;
  emailRequestSent: number;
  isSubmitted: number;
  reminderSent: number;
  dateCreated: Date;
  dateSubmitted: Date | null;
  dateReminded: Date | null;
  tripId?: number;
  tripGroupName?: string;
  tripDate?: Date;
}
interface GetFormResponseResponse {
  response: FormResponse,
  form: PpmFormWithQuestions
}

interface StaffEvalReportResponse {
  form: PpmFormWithQuestions,
  results: StaffEvalReportLine[]
}

interface UnsubmittedReportsResponse {
  responses: FormResponse[],
  trips: TripDetail[]
}

@Injectable({
  providedIn: 'root'
})
export class FormResponseService {

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private tripService: TripService
  ) { }

  readonly formResponseDateFields: DateField<FormResponse>[] = [
    { dateType: "utc", fieldName: "dateCreated" },
    { dateType: "utc", fieldName: "dateSubmitted" }
  ]

  readonly partnerPostTripSurveyDateFields: DateField<PartnerPostTripSurveyStatus>[] = [
    { dateType: "utc", fieldName: "dateCreated" },
    { dateType: "utc", fieldName: "dateSubmitted" },
    { dateType: "utc", fieldName: "dateReminded" },
  ]

  readonly tripLinkedSurveyDateFields: DateField<TripLinkedFormResponseSurveyRow>[] = [
    { dateType: "utc", fieldName: "dateCreated" },
    { dateType: "utc", fieldName: "dateSubmitted" },
    { dateType: "utc", fieldName: "dateReminded" },
    { dateType: "local", fieldName: "tripDate" },
  ]

  readonly formResponseReportDateFields: DateField<FormResponseReportLine>[] = [
    ...this.formResponseDateFields,
    { dateType: "utc", fieldName: "tripDate" }
  ]



  getEmptyResponse(): FormResponse {
    return {
      formId: 0,
      id: 0,
      isSubmitted: 0,
      dateCreated: new Date(1000, 0, 1)
    }
  }

  getFormResponse(id: number): Observable<GetFormResponseResponse> {
    return this.http.get<GetFormResponseResponse>(`${this.config.dataApiUrl}/forms/responses/${id.toString()}`)
  }

  submitForm(responseId: number, responses: Array<FormQuestionResponse & { optionId: number | null, optionText: string | null, options: { id: number, text: string, selected: boolean }[] }>) {

    const postData = responses.map(r => {
      const { optionText, optionId, options, ...rest } = r

      let selections: Partial<FormResponseOptionSelection>[] = optionId ? [{ formOptionId: optionId, text: optionText ?? "", formQuestionResponseId: r.id }] : []

      selections = [...selections, ...options.filter(o => o.selected).map(o => { return { formOptionId: o.id, text: o.text, formQuestionResponseId: r.id } })]

      return { ...rest, optionSelections: selections }
    })

    return this.http.post<FormQuestionResponse[]>(`${this.config.dataApiUrl}/forms/responses/${responseId.toString()}`, postData)
  }

  getResponseReportData(searchParams: FormResponseSearchParameters): Observable<FormResponseReportLine[]> {
    const { formId, ...params } = searchParams

    const queryParams = new HttpParams()
      .set('params', JSON.stringify(params))

    return this.http.get<FormResponseReportLine[]>(`${this.config.dataApiUrl}/forms/${formId.toString()}/reports/response-table`, { params: queryParams }).pipe(
      switchMap(result => {
        return of(DataTransform.replaceDatesArrayDirect(result, this.formResponseReportDateFields).map(r => {
         return {...r,
          trips: DataTransform.replaceDatesArrayDirect(r.trips, [{dateType: "local", fieldName: "tripDate"}])}
        }))
      })
    )
  }

  getStaffEvalReportData(searchParams: FormResponseSearchParameters): Observable<StaffEvalReportResponse> {
    const { formId, ...params } = searchParams
    const queryParams = new HttpParams()
      .set('params', JSON.stringify(params))

    return this.http.get<StaffEvalReportResponse>(`${this.config.dataApiUrl}/forms/${formId.toString()}/reports/staff-evals`, { params: queryParams })
  }

  getResponseReportDataForTrip(tripId: number) {
    return this.http.get<TripResponseReportData[]>(`${this.config.dataApiUrl}/forms/trip/${tripId.toString()}/reports/response-table`)
  }

  getPartnerPostTripSurveyStatusForTrip(tripId: number): Observable<PartnerPostTripSurveyStatus[]> {
    return this.http.get<PartnerPostTripSurveyStatus[]>(
      `${this.config.dataApiUrl}/forms/trip/${tripId.toString()}/partner-post-trip-surveys`
    ).pipe(
      switchMap((results) => of(
        DataTransform.replaceDatesArrayDirect(results, this.partnerPostTripSurveyDateFields)
      ))
    );
  }

  getSurveyResponsesByTripId(tripId: number): Observable<TripLinkedFormResponseSurveyRow[]> {
    return this.http.get<TripLinkedFormResponseSurveyRow[]>(
      `${this.config.dataApiUrl}/forms/trip/${tripId.toString()}/survey-responses-byTripId`
    ).pipe(
      switchMap((results) => of(
        DataTransform.replaceDatesArrayDirect(results, this.tripLinkedSurveyDateFields)
      ))
    );
  }

  sendHostPartnerSurveyManually(formResponseId: number): Observable<void> {
    return this.http.post<void>(
      `${this.config.dataApiUrl}/forms/responses/${formResponseId.toString()}/send-host-partner-survey-manually`,
      {},
    );
  }

  getEmptySurveysByTripDate(params: {
    tripStartDate: Date;
    tripEndDate: Date;
    tripIds?: number[];
    locationIds?: string[];
    formId?: number;
    contactResponderOnly?: boolean;
    tripStaffIds?: number[];
  }): Observable<TripLinkedFormResponseSurveyRow[]> {
    let httpParams = new HttpParams()
      .set('tripStartDate', params.tripStartDate.toISOString())
      .set('tripEndDate', params.tripEndDate.toISOString());
    if (params.tripIds != null && params.tripIds.length > 0) {
      httpParams = httpParams.set('tripIds', params.tripIds.join(','));
    }
    if (params.locationIds != null && params.locationIds.length > 0) {
      httpParams = httpParams.set('locationIds', params.locationIds.join(','));
    }
    if (params.formId != null && params.formId > 0) {
      httpParams = httpParams.set('formId', params.formId.toString());
    }
    if (params.contactResponderOnly === true) {
      httpParams = httpParams.set('contactResponderOnly', 'true');
    }
    if (params.tripStaffIds != null && params.tripStaffIds.length > 0) {
      httpParams = httpParams.set('tripStaffIds', params.tripStaffIds.join(','));
    }
    return this.http
      .get<TripLinkedFormResponseSurveyRow[]>(
        `${this.config.dataApiUrl}/forms/reports/unsubmitted-surveys`,
        { params: httpParams },
      )
      .pipe(
        switchMap((results) =>
          of(DataTransform.replaceDatesArrayDirect(results, this.tripLinkedSurveyDateFields)),
        ),
      );
  }

  getMyUnsubmittedResponses() {
    return this.http.get<UnsubmittedReportsResponse>(`${this.config.dataApiUrl}/forms/responses/staff/my/unsubmitted`).pipe(
      switchMap(result => {
        return of({
          responses: DataTransform.replaceDatesArrayDirect(result.responses, this.formResponseDateFields),
          trips: DataTransform.replaceDatesArrayDirect(result.trips, this.tripService.tripDetailDateFields)
        })

      })
    )
  }


  generateQuestionColumns(questions: FormQuestion[]) {
  
    const columns: FormQuestionColumn[] = []
  
    for (const question of questions) {

      let style: any = {}

      if (question.type === FormQuestionTypes.Text) {
        style = { 'min-width': "20rem" }
      }
      else if (question.type === FormQuestionTypes.AtlEval) {
        style = {'min-width': "15rem"}
      }

      columns.push({
        header: question.reportText ? question.reportText : question.text,
        longText: question.text,
        field: question.id.toString(),
        type: question.type,
        style: style 
      })
    }

    return columns
  }
}
